import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  createStyles,
  FormHelperText,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';

import React from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import TextField from '../../../text-field';

import { ExpandMoreOutlined } from '@material-ui/icons';
import clsx from 'clsx';

import { PersonPrimaryIdType } from '../../../../__generated__/pge-types';

import Dropdown from '../../../dropdown';
import { IDENTITY_VERIFICATION_PANEL_TYPES } from '.';
import { statesAndProvincesGrouping } from '../../../utility/state-dropdown';
import { BaseFormModel } from '../../../../hooks/useFormState.types';
import ITINTextField from '../../../alternate-id-text-field';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiOutlinedInput-root': {
        background: theme.palette.common.white,
      },
    },
    panelOpened: {
      background: colors.aliceBlue,
    },
    panelHeader: {
      fontSize: '1rem',
      color: colors.sparkBlue,
      '&.active': {
        color: colors.black,
        fontWeight: theme.typography.fontWeightBold,
      },
    },
    panelDetails: {
      padding: theme.spacing(2, 2),
    },
    marginTop: {
      marginTop: theme.spacing(3),
    },
    panelDetailsContainer: {
      padding: '0px 16px 16px',
    },
    panelSummary: {
      minHeight: '50px !important',
      paddingTop: 10,
      '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: '4px 0',
        minHeight: 28,
      },
    },
  }),
);

interface Props {
  form: BaseFormModel;
  activePanel: IDENTITY_VERIFICATION_PANEL_TYPES;
  setActivePanel: (e: IDENTITY_VERIFICATION_PANEL_TYPES) => void;
}

const AlternateIDVerification = ({
  form,
  activePanel,
  setActivePanel,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const ALTERNATIVE_ID = IDENTITY_VERIFICATION_PANEL_TYPES.ALTERNATIVE_ID;

  const { idType } = form.values;

  const isStateIdFieldEnabled = idType === PersonPrimaryIdType.Dl;

  const AllowedIDTypes: Record<string, any> = {
    [PersonPrimaryIdType.Dl]: `${t('DRIVERS_LICENSE_STATE_ID')}`,
    [PersonPrimaryIdType.Matr]: t('MATRICULA_ID'),
    [PersonPrimaryIdType.Pssprt]: t('PASSPORT'),
    [PersonPrimaryIdType.Resalien]: t('RESIDENT_ALIEN'),
    [PersonPrimaryIdType.Visa]: t('VISA_ID'),
    [PersonPrimaryIdType.Military]: t('MILITARY_ID'),
    [PersonPrimaryIdType.Ortrib]: t('OR_TRIBAL_ID'),
    [PersonPrimaryIdType.Itin]: t('ITIN'),
  };

  const idValueFieldLabel: string = AllowedIDTypes[idType as string];

  return (
    <Accordion
      expanded={activePanel === ALTERNATIVE_ID}
      onChange={async () => {
        if (activePanel === ALTERNATIVE_ID) {
          return false;
        }

        setActivePanel(ALTERNATIVE_ID);

        await Promise.all([
          form.setValue('idType', ''),
          form.setValue('idState', ''),
          form.setValue('idValue', ''),
        ]);

        form.setError('idType', null);
        form.setError('idState', null);
        form.setError('idValue', null);
      }}
      className={clsx({
        [classes.panelOpened]: activePanel === ALTERNATIVE_ID,
        [classes.root]: true,
      })}
    >
      <AccordionSummary
        expandIcon={activePanel !== ALTERNATIVE_ID && <ExpandMoreOutlined />}
        aria-controls="panel2bh-content"
        id="panel2bh-header"
        data-testid="altid-summary-panel"
        className={classes.panelSummary}
      >
        <Typography
          className={clsx({
            [classes.panelHeader]: true,
            active: activePanel === ALTERNATIVE_ID,
          })}
        >
          {t('SSM_VERIFY_ALTERNATIVE_ID_PANEL_TITLE')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        data-testid="altid-accordion-details"
        className={classes.panelDetailsContainer}
      >
        <Grid container item className={classes.panelDetails}>
          <Grid item xs={12} md={12}>
            {form.values.idType !== PersonPrimaryIdType.Ssn && (
              <Dropdown
                label="Please Select"
                selectionList={Object.keys(AllowedIDTypes).map(
                  (key: string) => ({
                    value: key,
                    label: AllowedIDTypes[key],
                  }),
                )}
                {...form.props('idType')}
                error={Boolean(form.errors.idType)}
                onChange={async (e: any) => {
                  await Promise.all([
                    form.setValue('idType', e.target.value),
                    form.setValue(
                      'idState',
                      e.target.value === 'DL' ? 'OR' : '',
                    ),
                    form.setValue('idValue', ''),
                  ]);
                  form.setError('idState', null);
                  form.setError('idValue', null);
                  form.onChange(e);
                }}
              />
            )}
            {form.errors.idType && (
              <FormHelperText error={true}>{form.errors.idType}</FormHelperText>
            )}
          </Grid>

          {isStateIdFieldEnabled && (
            <Grid item xs={12} md={12} className={classes.marginTop}>
              <FormControl
                error={Boolean(form.errors.idState)}
                style={{ width: '100%', background: 'transparent' }}
                variant="outlined"
              >
                <InputLabel id="id-sub-type-state-label">
                  {t('STATE')}
                </InputLabel>
                <Select
                  labelId="id-sub-type-state-label"
                  name="idState"
                  inputProps={{
                    'data-testid': 'state-field',
                    style: { background: '#FFF' },
                  }}
                  value={form.values.idState}
                  onChange={form.onChange}
                  error={Boolean(form.errors.idState)}
                  autoWidth
                  onBlur={form.onBlur}
                  style={{ width: '100%' }}
                  label={t('STATE')}
                  variant="outlined"
                  native
                >
                  <option key={'OR'} value={'OR'}>
                    {'OR'}
                  </option>
                  {statesAndProvincesGrouping.unitedStates
                    .filter((v: string) => v !== 'OR')
                    .map((v: string) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                </Select>
                <FormHelperText>{form.errors.idState}</FormHelperText>
              </FormControl>
            </Grid>
          )}

          {form.values.idType !== '' &&
            form.values.idType !== PersonPrimaryIdType.Itin && (
              <Grid item xs={12} md={12} className={classes.marginTop}>
                <TextField
                  inputProps={{
                    maxLength: 15,
                    style: { background: '#FFF' },
                  }}
                  style={{
                    width: '100%',
                    background: 'transparent',
                  }}
                  name="idValue"
                  {...form.props('idValue')}
                  error={Boolean(form.errors.idValue)}
                  label={idValueFieldLabel}
                  delayedTextMask={true}
                />
              </Grid>
            )}

          {form.values.idType !== '' &&
            form.values.idType === PersonPrimaryIdType.Itin && (
              <Grid item xs={12} md={12} className={classes.marginTop}>
                <ITINTextField
                  name="idValue"
                  label={idValueFieldLabel}
                  style={{
                    width: '100%',
                    background: 'transparent',
                  }}
                  {...form.props('idValue')}
                  error={Boolean(form.errors.idValue)}
                />
              </Grid>
            )}

          <Grid item xs={12} md={12}>
            <Box mt={2}>
              <Typography variant="subtitle1">
                {t('SSM_VERIFY_CUST_ALTERNATE_ID_DISCLAIMER')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default AlternateIDVerification;
